import { useState, useEffect } from 'react'
import { FiSearch, FiTrash2, FiEdit2, FiEye, FiEyeOff, FiChevronDown, FiLoader, FiFlag } from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'
import WebsiteActionModal from '../../components/globalAdmin/WebsiteActionModal'
import SectionActionModal from '../../components/globalAdmin/SectionActionModal'
import ReportsModal from '../../components/globalAdmin/ReportsModal'
import PublishingLimitsModal from '../../components/globalAdmin/PublishingLimitsModal'

export default function GlobalAdminWebsites() {
  const [websites, setWebsites] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [expandedWebsite, setExpandedWebsite] = useState(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [pagination, setPagination] = useState({ total: 0, pages: 0 })

  // Modal states
  const [websiteActionModal, setWebsiteActionModal] = useState({ isOpen: false, website: null, action: null })
  const [sectionActionModal, setSectionActionModal] = useState({ isOpen: false, section: null, website: null, action: null })
  const [reportsModal, setReportsModal] = useState({ isOpen: false, website: null })
  const [publishingLimitsModal, setPublishingLimitsModal] = useState({ isOpen: false })

  // Loading states for actions
  const [actingOnId, setActingOnId] = useState(null)
  const [expandingId, setExpandingId] = useState(null)

  const fetchWebsites = async () => {
    try {
      setLoading(true)
      const params = { page, limit }
      if (searchQuery) params.search = searchQuery
      if (filterStatus !== 'all') params.status = filterStatus
      
      console.log('[GlobalAdminWebsites] Fetching websites with params:', params)
      const response = await axios.get('/global-admin/websites', { params })
      console.log('[GlobalAdminWebsites] Websites fetched successfully:', response.data)
      setWebsites(response.data.websites || [])
      setPagination(response.data.pagination || { total: 0, pages: 0 })
    } catch (error) {
      console.error('[GlobalAdminWebsites] Error fetching websites:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.response?.data?.message,
        data: error.response?.data,
        fullError: error.message
      })
      toast.error(error.response?.data?.message || 'Failed to load websites')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWebsites()
  }, [page, limit, filterStatus, searchQuery])

  const handleToggleExpand = async (website) => {
    if (expandedWebsite?._id === website._id) {
      setExpandedWebsite(null)
    } else {
      setExpandingId(website._id)
      try {
        const response = await axios.get(`/global-admin/websites/${website._id}/sections`)
        setExpandedWebsite({ ...website, sections: response.data.sections })
      } catch (error) {
        console.error('Error fetching sections:', error)
        toast.error('Failed to load sections')
      } finally {
        setExpandingId(null)
      }
    }
  }

  const handleDisableWebsite = (website) => {
    setWebsiteActionModal({
      isOpen: true,
      website,
      action: website.globalAdminDisabled ? 'enable' : 'disable'
    })
  }

  const handleDeleteWebsite = (website) => {
    setWebsiteActionModal({
      isOpen: true,
      website,
      action: 'delete'
    })
  }

  const handleDisableSection = (section, website) => {
    setSectionActionModal({
      isOpen: true,
      section,
      website,
      action: section.disabled ? 'enable' : 'disable'
    })
  }

  const handleWebsiteAction = async (website, action, reason) => {
    setActingOnId(website._id)
    try {
      if (action === 'disable') {
        await axios.put(`/global-admin/websites/${website._id}/disable`, { reason })
        toast.success('Website disabled')
      } else if (action === 'enable') {
        await axios.put(`/global-admin/websites/${website._id}/enable`)
        toast.success('Website enabled')
      } else if (action === 'delete') {
        if (!window.confirm('Are you sure? This action cannot be undone.')) return
        await axios.delete(`/global-admin/websites/${website._id}`)
        toast.success('Website deleted')
      }
      fetchWebsites()
      setWebsiteActionModal({ isOpen: false, website: null, action: null })
    } catch (error) {
      console.error('Error performing action:', error)
      toast.error(error.response?.data?.message || 'Action failed')
    } finally {
      setActingOnId(null)
    }
  }

  const handleSectionAction = async (section, action, reason) => {
    setActingOnId(section._id)
    try {
      if (action === 'disable') {
        await axios.put(`/global-admin/websites/sections/${section._id}/disable`, { reason })
        toast.success('Section disabled')
      } else if (action === 'enable') {
        await axios.put(`/global-admin/websites/sections/${section._id}/enable`)
        toast.success('Section enabled')
      } else if (action === 'delete') {
        if (!window.confirm('Are you sure? This will remove the section from the website.')) return
        await axios.delete(`/global-admin/websites/sections/${section._id}`)
        toast.success('Section deleted')
      }
      if (expandedWebsite?._id === sectionActionModal.website._id) {
        handleToggleExpand(sectionActionModal.website)
      }
      setSectionActionModal({ isOpen: false, section: null, website: null, action: null })
    } catch (error) {
      console.error('Error performing action:', error)
      toast.error(error.response?.data?.message || 'Action failed')
    } finally {
      setActingOnId(null)
    }
  }

  const getStatusColor = (website) => {
    if (website.globalAdminDisabled) return 'bg-red-100 text-red-800 border-red-300'
    if (website.status === 'published') return 'bg-green-100 text-green-800 border-green-300'
    if (website.status === 'draft') return 'bg-yellow-100 text-yellow-800 border-yellow-300'
    return 'bg-gray-100 text-gray-800 border-gray-300'
  }

  const getStatusLabel = (website) => {
    if (website.globalAdminDisabled) return `Disabled by Admin (${website.globalAdminDisabledReason || 'No reason provided'})`
    return website.status.charAt(0).toUpperCase() + website.status.slice(1)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Website Management</h2>
          <p className="text-gray-600 mt-1">Monitor, control, and moderate all websites on the platform</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPublishingLimitsModal({ isOpen: true })}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
          >
            ⚙️ Publishing Limits
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="flex gap-4 flex-wrap">
        <div className="flex-1 min-w-64 relative">
          <FiSearch className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by name, slug, or user email..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setPage(1)
            }}
            className="input-field pl-10"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value)
            setPage(1)
          }}
          className="input-field px-4"
        >
          <option value="all">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="disabled">Disabled</option>
        </select>
        <select
          value={limit}
          onChange={(e) => {
            setLimit(parseInt(e.target.value))
            setPage(1)
          }}
          className="input-field px-4"
        >
          <option value={10}>10 per page</option>
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
        </select>
      </div>

      {/* Websites List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : websites.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500 text-lg">No websites found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {websites.map((website) => (
            <div key={website._id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Website Header */}
              <div className="p-5 border-b border-gray-100">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{website.name}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(website)}`}>
                        {getStatusLabel(website)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Slug:</span> {website.slug}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">User:</span> {website.userId?.email || 'Unknown'}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Category:</span> {website.category}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Sections:</span> {website.sections?.length || 0}
                    </p>
                    {website.globalAdminDisabled && website.globalAdminDisabledReason && (
                      <p className="text-sm text-red-600 mt-2">
                        <span className="font-medium">Reason:</span> {website.globalAdminDisabledReason}
                      </p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleToggleExpand(website)}
                      disabled={expandingId === website._id}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                      title="View/Edit Sections"
                    >
                      {expandingId === website._id ? (
                        <FiLoader className="w-5 h-5 animate-spin" />
                      ) : (
                        <FiChevronDown className={`w-5 h-5 transition-transform ${expandedWebsite?._id === website._id ? 'rotate-180' : ''}`} />
                      )}
                    </button>
                    <button
                      onClick={() => handleDisableWebsite(website)}
                      disabled={actingOnId === website._id}
                      className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                        website.globalAdminDisabled
                          ? 'hover:bg-green-100 text-green-600'
                          : 'hover:bg-red-100 text-red-600'
                      }`}
                      title={website.globalAdminDisabled ? 'Enable Website' : 'Disable Website'}
                    >
                      {actingOnId === website._id ? (
                        <FiLoader className="w-5 h-5 animate-spin" />
                      ) : website.globalAdminDisabled ? (
                        <FiEye className="w-5 h-5" />
                      ) : (
                        <FiEyeOff className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setReportsModal({ isOpen: true, website })
                      }}
                      className="p-2 hover:bg-orange-100 text-orange-600 rounded-lg transition-colors"
                      title="View Reports"
                    >
                      <FiFlag className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteWebsite(website)}
                      disabled={actingOnId === website._id}
                      className="p-2 hover:bg-red-100 text-red-600 rounded-lg transition-colors disabled:opacity-50"
                      title="Delete Website"
                    >
                      {actingOnId === website._id ? (
                        <FiLoader className="w-5 h-5 animate-spin" />
                      ) : (
                        <FiTrash2 className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Expanded Sections */}
              {expandedWebsite?._id === website._id && (
                <div className="bg-gray-50 p-5 space-y-3">
                  {expandedWebsite.sections && expandedWebsite.sections.length > 0 ? (
                    expandedWebsite.sections.map((section) => (
                      <div key={section._id} className="bg-white p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{section.title}</h4>
                            <p className="text-sm text-gray-600">{section.description}</p>
                            {section.disabled && (
                              <p className="text-sm text-red-600 font-medium mt-1">Disabled by Admin</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleDisableSection(section, website)}
                              disabled={actingOnId === section._id}
                              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50 ${
                                section.disabled
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-red-100 text-red-700 hover:bg-red-200'
                              }`}
                            >
                              {actingOnId === section._id ? (
                                <FiLoader className="w-4 h-4 animate-spin inline" />
                              ) : section.disabled ? (
                                'Enable'
                              ) : (
                                'Disable'
                              )}
                            </button>
                            <button
                              onClick={() => handleDeleteWebsite(website)}
                              disabled={actingOnId === section._id}
                              className="px-3 py-1.5 rounded text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors disabled:opacity-50"
                            >
                              {actingOnId === section._id ? (
                                <FiLoader className="w-4 h-4 animate-spin inline" />
                              ) : (
                                'Delete'
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-500">No sections in this website</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, pagination.total)} of {pagination.total} websites
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              First
            </button>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            {/* Page Numbers */}
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                let pageNum
                if (pagination.pages <= 5) {
                  pageNum = i + 1
                } else if (page <= 3) {
                  pageNum = i + 1
                } else if (page >= pagination.pages - 2) {
                  pageNum = pagination.pages - 4 + i
                } else {
                  pageNum = page - 2 + i
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      page === pageNum
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>
            
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === pagination.pages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() => setPage(pagination.pages)}
              disabled={page === pagination.pages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Last
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <WebsiteActionModal
        isOpen={websiteActionModal.isOpen}
        website={websiteActionModal.website}
        action={websiteActionModal.action}
        onClose={() => setWebsiteActionModal({ isOpen: false, website: null, action: null })}
        onConfirm={handleWebsiteAction}
      />

      <SectionActionModal
        isOpen={sectionActionModal.isOpen}
        section={sectionActionModal.section}
        website={sectionActionModal.website}
        action={sectionActionModal.action}
        onClose={() => setSectionActionModal({ isOpen: false, section: null, website: null, action: null })}
        onConfirm={handleSectionAction}
      />

      <ReportsModal
        isOpen={reportsModal.isOpen}
        website={reportsModal.website}
        onClose={() => setReportsModal({ isOpen: false, website: null })}
      />

      <PublishingLimitsModal
        isOpen={publishingLimitsModal.isOpen}
        onClose={() => setPublishingLimitsModal({ isOpen: false })}
      />
    </div>
  )
}
